import { useCallback, useState } from "react";
import type { LayoutChangeEvent } from "react-native";

/**
 * Fits as many columns as stay above `minCardWidth` inside the caller's own
 * measured width, capped at `maxColumns`. Measured rather than taken from
 * window width because these grids sit inside WebLayout beside a sidebar that
 * varies between a collapsed rail and a full panel — window width alone
 * doesn't say how much room is actually left for cards.
 *
 * `gutter` is the grid's horizontal spacing unit: it's spent once as the
 * FlatList's own `contentContainerStyle` padding and again per column as each
 * item's padding, so both the once-off and the per-column cost need to come
 * out of the width budget before dividing — otherwise cards below the
 * threshold end up narrower than `minCardWidth` right where a column is added.
 */
export function useResponsiveGridColumns(
  minCardWidth: number,
  maxColumns: number,
  gutter: number,
): { numColumns: number; isMeasured: boolean; onLayout: (e: LayoutChangeEvent) => void } {
  const [width, setWidth] = useState(0);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setWidth(e.nativeEvent.layout.width);
  }, []);
  const fits = Math.floor((width - gutter) / (minCardWidth + gutter));
  return { numColumns: Math.min(Math.max(fits, 1), maxColumns), isMeasured: width > 0, onLayout };
}
